import {
  AlertTriangle,
  LayoutDashboard,
  Users,
  FolderOpen,
  FileText,
  Receipt,
  FileSignature,
  ClipboardList,
  UserCircle,
  Settings,
  TrendingUp,
  CalendarDays,
  Sparkles,
  CheckSquare,
  Camera,
  MessageCircle,
  Image,
  HelpCircle,
  Inbox,
  Zap,
} from 'lucide-react'
import { AUTH, hasAnyAuthority } from '@/shared/access/accessModel'

/**
 * The app's navigation table, and with it the single source of truth for WHICH
 * ADR-036 AUTHORITIES can reach each route.
 *
 * It lived inside `AppLayout` until a second consumer appeared: the client
 * detail page asks "can this user reach the Emails area?" before offering an
 * in-app compose. Importing that from `AppLayout` made a feature depend on the
 * app shell for a plain data table, and the alternative — a second copy of the
 * role list next to the compose hook — is the copy that goes stale. So the
 * table moved here, where both can read it and neither owns it.
 *
 * Nav items store TRANSLATION KEYS rather than literal labels: the nav calls
 * `t(labelKey)` at render time, so switching language re-renders the sidebar
 * with the new strings. Same for group names — `groupKey` resolves to
 * `nav.group.*` in `common.json`.
 *
 * `roles: null` means "every authenticated member".
 *
 * ## Reading the lists (ADR-036, slice 3)
 *
 * The names are authorities, not the old role enum: four levels, exactly one per
 * membership, plus two switches spelled WITHOUT a `ROLE_` prefix so no list here can
 * absorb one by accident. `/finance` is the one row gated on a switch — that is axis B
 * working as designed, and it is why an Admin's sidebar grows a Finance row the moment
 * an Owner turns "sees finances" on, with no change of level.
 *
 * Each row matches what the backing endpoints actually admit post-slice-2. Where they
 * differ deliberately the row carries a comment; a row that quietly over-promises turns
 * into a 403 the member cannot act on, which is worse than a hidden link.
 */
export type NavItem = {
  to: string
  labelKey: string
  /** A lucide icon. `strokeWidth` is part of the contract since IN-18 — the sidebar
   *  renders every glyph at a stroke heavy enough to survive a dark background. */
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>
  roles: string[] | null
  /**
   * Authorities that REMOVE the row even when `roles` admits it — the negative half
   * of the table, and until 2026-09-08 the thing it could not say at all.
   *
   * It exists for exactly one server sentence, and it exists so the row can copy that
   * sentence rather than approximate it: `GET /clients` is
   * `isAuthenticated() and !hasAuthority('SCOPE_ASSIGNED_ONLY')`. A positive-only list
   * has two ways to render that and both are wrong — name CREW and the row 403s for a
   * scoped member, omit CREW and it hides a list the server serves to every unscoped
   * one (the `/tasks` disagreement of T1, one level up).
   *
   * **This is not a fifth boundary and must not become one.** The switch gates no
   * route (`roleGuardMatrix` says so in as many words); it appears here only where the
   * ENDPOINT behind the row already refuses a scoped member, so the row and its data
   * agree. Adding a `deniedRoles` a server does not have would be a UI gate stricter
   * than its boundary — the invisible class this whole file exists to stop.
   */
  deniedRoles?: string[]
  /**
   * Sidebar PRESENTATION only — which of two rows that show the same data a member sees
   * (RB-28 item 9, D56). `'scoped'`: the row is the SCOPE_ASSIGNED_ONLY member's view, shown
   * to them and to no one else. `'unscoped'`: the row is the whole-studio view, hidden from a
   * scoped member (the row they get instead is the `'scoped'` twin).
   *
   * Deliberately NOT `deniedRoles`: that field copies a server refusal and is pinned to the
   * single `/clients` row by `roleGuardMatrix`; this one removes nothing a member may reach
   * (`canReachRoute` ignores it, and the routes stay open for deep links and notifications).
   * It only stops one thing appearing twice under two names — a scoped Crew member saw
   * "Projects" and "My Projects" for the same single list.
   */
  scopeView?: 'scoped' | 'unscoped'
}

export const navGroups: { groupKey: string; items: NavItem[] }[] = [
  {
    groupKey: 'nav.group.operations',
    items: [
      // Open to EVERY level since 2026-09-08 (DESIGN-what-is-crew-for.md, Q2 finding 1;
      // Andras: "I agree with the recommendation build accordingly, go"). Gating the
      // PAGE by level was the exclusion with no case behind it: the only thing on it
      // that has one is money, money is gated by CAN_OPEN_FINANCE already, and hiding the
      // page is what made an urgent `@!` mention unreachable and forced `/attention`
      // into existence. Each widget now gates itself on the authorities its own
      // endpoint admits — see `WIDGET_ACCESS` in DashboardPage.
      { to: '/dashboard',    labelKey: 'nav.dashboard',   icon: LayoutDashboard, roles: null },
      // The Dashboard's Needs-Attention half, standalone and open to EVERY member
      // (C5-1). `roles: null` is not a widening: `/api/v1/attention` is
      // isAuthenticated() and scopes its response to the caller, so a Crew member
      // opening it sees the items addressed to them and nothing else. It stays a row
      // of its own now that the Dashboard is open — a deep link to the half that is
      // addressed to a person, not the workaround it started as.
      { to: '/attention',    labelKey: 'nav.attention',   icon: AlertTriangle,   roles: null },
      { to: '/inquiries',    labelKey: 'nav.inquiries',   icon: Sparkles,        roles: [AUTH.OWNER, AUTH.ADMIN] },
      { to: '/emails',       labelKey: 'nav.emails',      icon: Inbox,           roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW] },
      // One entry per thing, named by what the viewer sees (RB-28 item 9, D56 — Andras: "If it's
      // scoped Crew we need only My Projects, if it's not scoped Crew then Projects"). A scoped
      // Crew member gets "My Projects" / "My Galleries" and no whole-studio twin; an unscoped one
      // gets "Projects" / "Galleries" and no "My …" twin. The `MY_*` routes stay open to every
      // Crew member (deep links, the home-page preference); only the sidebar row is chosen.
      { to: '/projects',     labelKey: 'nav.projects',    icon: FolderOpen,      roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW, AUTH.BOOKKEEPER], scopeView: 'unscoped' },
      { to: '/galleries',    labelKey: 'nav.galleries',   icon: Image,           roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW], scopeView: 'unscoped' },
      { to: '/my-projects',  labelKey: 'nav.myProjects',  icon: FolderOpen,      roles: [AUTH.CREW], scopeView: 'scoped' },
      { to: '/my-galleries', labelKey: 'nav.myGalleries', icon: Image,           roles: [AUTH.CREW], scopeView: 'scoped' },
      { to: '/calendar',     labelKey: 'nav.calendar',    icon: CalendarDays,    roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW] },
      { to: '/shoots',       labelKey: 'nav.shoots',      icon: Camera,          roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW] },
      { to: '/automations',  labelKey: 'nav.automations', icon: Zap,             roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW] },
      // Crew are NOT here, and this deliberately REVERSES decision #7 (`41171d53`,
      // "yes, they should see Tasks too") — IN-15, Andras 2026-09-08:
      //
      //   "I guess keeping only My Tasks makes the most sense, but let's align that UI
      //    to how Tasks work, but only show them their items"
      //
      // #7 existed because Crew could not reach a decent task screen; `/my-tasks` now
      // IS the `/tasks` board scoped to the member (see `TasksPage`'s `scope` prop), so
      // it answers that need better than a second row did, and it is the row that
      // survives — it is already their `ROLE_DEFAULT_HOME`. Two task rows for one person
      // was the finding, not the fix.
      //
      // This is a row narrower than its endpoint, which the `deniedRoles` note above
      // warns about — and it is allowed here for the one reason that note demands: the
      // data is not hidden. Everything `GET /tasks` would serve a Crew member about
      // their own work is on `/my-tasks`, and the `/tasks` ROUTE stays open to them
      // (`homePages.ts`) so `?highlight=<id>` notification deep links still land.
      { to: '/tasks',        labelKey: 'nav.tasks',       icon: CheckSquare,     roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.BOOKKEEPER] },
      { to: '/my-tasks',     labelKey: 'nav.myTasks',     icon: CheckSquare,     roles: [AUTH.CREW] },
      { to: '/chat',         labelKey: 'nav.chat',        icon: MessageCircle,   roles: null },
    ],
  },
  {
    groupKey: 'nav.group.documents',
    items: [
      // Unscoped Crew belong here (DESIGN-what-is-crew-for.md, Q2 finding 2). The
      // server already serves them the list — `GET /clients` is
      // `isAuthenticated() and !hasAuthority('SCOPE_ASSIGNED_ONLY')` — so only the
      // sidebar was hiding it, the same shape as the `/tasks` disagreement (T1).
      // A photographer needs the client's phone number on the day.
      //
      // The `deniedRoles` half is not decoration: a SCOPED Crew member is refused by
      // that same annotation, and `ClientServiceImpl` answers them with the minimal
      // view elsewhere. Offering them the row would be a link to a 403.
      { to: '/clients',        labelKey: 'nav.clients',        icon: Users,         roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW], deniedRoles: [AUTH.SCOPE_ASSIGNED_ONLY] },
      { to: '/offers',         labelKey: 'nav.offers',         icon: FileText,      roles: [AUTH.OWNER, AUTH.ADMIN] },
      { to: '/contracts',      labelKey: 'nav.contracts',      icon: FileSignature, roles: [AUTH.OWNER, AUTH.ADMIN] },
      // Finance keeps the books: the invoice list is theirs, and `/api/v1/invoices`
      // admits BOOKKEEPER. Crew reach a project's invoices from the project, not here.
      { to: '/invoices',       labelKey: 'nav.invoices',       icon: Receipt,       roles: [AUTH.OWNER, AUTH.ADMIN, AUTH.BOOKKEEPER] },
      { to: '/questionnaires', labelKey: 'nav.questionnaires', icon: ClipboardList, roles: [AUTH.OWNER, AUTH.ADMIN] },
    ],
  },
  {
    groupKey: 'nav.group.admin',
    items: [
      { to: '/finance',  labelKey: 'nav.finance',  icon: TrendingUp, roles: [AUTH.CAN_OPEN_FINANCE] },
      { to: '/team',     labelKey: 'nav.team',     icon: UserCircle,  roles: null },
      { to: '/settings', labelKey: 'nav.settings', icon: Settings,    roles: null },
      { to: '/help',     labelKey: 'nav.help',     icon: HelpCircle,  roles: null },
    ],
  },
]

/** Flat list used for role-filtering. */
export const allNavItems: NavItem[] = navGroups.flatMap(g => g.items)

/**
 * The authorities that may reach `to`, or `null` for "every authenticated member".
 * Returns `[]` for a route that is not in the table at all — deny by default,
 * so a typo'd path hides the affordance rather than exposing it.
 */
export function rolesForRoute(to: string): string[] | null {
  const item = allNavItems.find(i => i.to === to)
  if (!item) return []
  return item.roles
}

/** The authorities that REMOVE `to` from a member's sidebar. Empty for most rows. */
export function deniedRolesForRoute(to: string): string[] {
  return allNavItems.find(i => i.to === to)?.deniedRoles ?? []
}

/**
 * Whether a member holding `roles` may reach `to`.
 *
 * `deniedRoles` wins over `roles`, which is the only order that can express the
 * server's `isAuthenticated() and !hasAuthority(...)` shape — see the field's JSDoc.
 */
export function canReachRoute(to: string, roles: readonly string[]): boolean {
  const item = allNavItems.find(i => i.to === to)
  if (!item) return false
  if (item.deniedRoles && hasAnyAuthority(roles, item.deniedRoles)) return false
  if (item.roles === null) return true
  return hasAnyAuthority(roles, item.roles)
}

/**
 * Whether the SIDEBAR shows the row for `to` to a member holding `roles`: reachable
 * (`canReachRoute`) and, for the rows that exist in two scope-specific forms, the right form
 * (`scopeView`). Use this to draw the sidebar and `canReachRoute` for "may they open it".
 */
export function isNavRowShown(to: string, roles: readonly string[]): boolean {
  if (!canReachRoute(to, roles)) return false
  const view = allNavItems.find(i => i.to === to)?.scopeView
  if (!view) return true
  const scoped = hasAnyAuthority(roles, [AUTH.SCOPE_ASSIGNED_ONLY])
  return view === 'scoped' ? scoped : !scoped
}
